/**
 * batch-46 adres-reis (FO-390) — verhuisdatum-bereik.
 *
 * De verhuisdatum op een ADRES-mutatie mag maximaal 3 maanden in het verleden en
 * 12 maanden in de toekomst liggen (spec 93 §3.1). Deze grens is gedeeld door de
 * portal-form (inline validatie), de submit-API en de backoffice-dialog zodat er
 * geen client/server-drift ontstaat - één bron van waarheid.
 *
 * Pure functies op yyyy-mm-dd-strings (ISO-kalenderdag). De aanroeper levert de
 * "vandaag"-dag in Europe/Amsterdam (CLAUDE.md): niet uit `process.tz` afleiden.
 */

export const VERHUISDATUM_MAX_MAANDEN_TERUG = 3
export const VERHUISDATUM_MAX_MAANDEN_VOORUIT = 12

export interface VerhuisdatumBereik {
  /** Vroegst toegestane verhuisdatum (yyyy-mm-dd, inclusief). */
  minIso: string
  /** Laatst toegestane verhuisdatum (yyyy-mm-dd, inclusief). */
  maxIso: string
}

function parseIsoDate(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return null
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) }
}

/** Aantal dagen in maand `m1` (1..12) van jaar `y`. */
function dagenInMaand(y: number, m1: number): number {
  return new Date(Date.UTC(y, m1, 0)).getUTCDate()
}

function pad(n: number, len: number): string {
  return String(n).padStart(len, '0')
}

/**
 * Tel `n` (mag negatief) kalendermaanden op bij een yyyy-mm-dd-datum. Klemt de
 * dag naar de laatste dag van de doelmaand (31 mei − 3 mnd → 28/29 feb), zodat de
 * grens nooit ongewild een paar dagen doorrolt. Geeft de invoer ongewijzigd terug
 * bij een onparsebare datum.
 */
export function addMaandenIso(iso: string, n: number): string {
  const p = parseIsoDate(iso)
  if (!p) return iso
  const totaalMaanden = p.y * 12 + (p.m - 1) + n
  const y = Math.floor(totaalMaanden / 12)
  const m1 = (totaalMaanden % 12) + 1
  const d = Math.min(p.d, dagenInMaand(y, m1))
  return `${pad(y, 4)}-${pad(m1, 2)}-${pad(d, 2)}`
}

/** Toegestaan verhuisdatum-bereik rond een NL-kalenderdag (yyyy-mm-dd). */
export function verhuisdatumBereik(vandaagIso: string): VerhuisdatumBereik {
  return {
    minIso: addMaandenIso(vandaagIso, -VERHUISDATUM_MAX_MAANDEN_TERUG),
    maxIso: addMaandenIso(vandaagIso, VERHUISDATUM_MAX_MAANDEN_VOORUIT),
  }
}

/**
 * Ligt `iso` binnen [vandaag − 3 mnd, vandaag + 12 mnd] (inclusief)? ISO-strings
 * yyyy-mm-dd zijn lexicografisch == chronologisch, dus een string-vergelijking
 * volstaat.
 */
export function verhuisdatumBinnenBereik(iso: string, vandaagIso: string): boolean {
  const p = parseIsoDate(iso)
  if (!p) return false
  const { minIso, maxIso } = verhuisdatumBereik(vandaagIso)
  return iso >= minIso && iso <= maxIso
}

/** Is `iso` (yyyy-mm-dd) een toekomstige dag t.o.v. `vandaagIso`? */
export function isToekomstigeDag(iso: string, vandaagIso: string): boolean {
  return iso > vandaagIso
}
